import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { useLoginMutation } from './useAuthMutations';
import { getDashboardRoute } from '@utils/routing';
import { useAuthStore } from '@stores/auth.store';
import { ENV } from '@/configs/env';

const trim = (v) => String(v ?? '').trim();

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidPhone = (v) => /^[+()\d\s-]{7,20}$/.test(v);

export const useSignin = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const loginMutation = useLoginMutation();

    const onFinish = async (values) => {
        try {
            const emailInput = trim(values?.email);
            const phoneInput = trim(values?.phone);

            if (!emailInput && !phoneInput) {
                const msg = 'Enter your email or phone number.';
                form.setFields([
                    { name: 'email', errors: [msg] },
                    { name: 'phone', errors: [msg] }
                ]);
                showMessage('error', msg);
                return;
            }

            if (emailInput && !isValidEmail(emailInput)) {
                form.setFields([{ name: 'email', errors: ['Please enter a valid email address.'] }]);
                showMessage('error', 'Please enter a valid email address.');
                return;
            }
            if (phoneInput && !isValidPhone(phoneInput)) {
                form.setFields([{ name: 'phone', errors: ['Please enter a valid phone number.'] }]);
                showMessage('error', 'Please enter a valid phone number.');
                return;
            }

            // Resolve full credentials: user may supply only one side when portal env provides the other.
            let loginEmail = emailInput || ENV.CLIENT_EMAIL || '';
            let loginPhone = phoneInput || ENV.CLIENT_PHONE || '';

            if (!loginEmail) {
                const msg =
                    'Email is required unless this portal is configured with VITE_CLIENT_EMAIL (for phone-only sign-in).';
                form.setFields([{ name: 'email', errors: [msg] }]);
                showMessage('error', msg);
                return;
            }
            if (!loginPhone) {
                const msg =
                    'Phone is required unless this portal is configured with VITE_CLIENT_PHONE (for email-only sign-in).';
                form.setFields([{ name: 'phone', errors: [msg] }]);
                showMessage('error', msg);
                return;
            }

            // Client-gated access: if env values exist, only allow exact matches for fields the user actually entered.
            if (emailInput && ENV.CLIENT_EMAIL && emailInput.toLowerCase() !== ENV.CLIENT_EMAIL.toLowerCase()) {
                form.setFields([{ name: 'email', errors: ['This email is not authorized.'] }]);
                showMessage('error', 'This email is not authorized.');
                return;
            }
            if (phoneInput && ENV.CLIENT_PHONE && phoneInput !== ENV.CLIENT_PHONE) {
                form.setFields([{ name: 'phone', errors: ['This phone number is not authorized.'] }]);
                showMessage('error', 'This phone number is not authorized.');
                return;
            }

            await loginMutation.mutateAsync({
                email: loginEmail,
                // Backend still expects `password`; we use the resolved phone value.
                password: loginPhone
            });

            if (parsedValues.remember) {
                localStorage.setItem('remember_me', 'true');
            }

            const { role, user } = useAuthStore.getState();
            const userRole = role || user?.role;

            const dashboardRoute = getDashboardRoute(userRole);
            navigate(dashboardRoute);
        } catch (error) {
            const message =
                error?.message ||
                (typeof error === 'string' ? error : 'Something went wrong. Please try again.');
            showMessage('error', message, 6);
        }
    };

    return {
        form,
        loading: loginMutation.isPending,
        onFinish
    };
};
